/**
 * Created by Alex on 12/18/2024.
 * Description:
 * append entity(s) to notes selected
 * ------ maintenance history ------
 * 12/25/2024 Alex Xia Added UI related logics 
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { AppendEntitiesDialogService } from './append-entities-dialog.component.service';
import { catchError } from 'rxjs/operators';
import { AppendEntitiesDialogType } from './append-entities-dialog.model';
import { EntityBrief } from '../../tamalelibs/models/entity-brief.model';
import { TemplateControlConfig } from '../template/template-control-config.model';
import { NoteActions } from '../../tamalelibs/constants/business.constants';
import { TemplateControlValue } from '../../tamalelibs/models/template-control-value.model';
import { select, Store } from '@ngrx/store';
import { APPEND_ENTITIES, APPEND_ALL_ENTITIES_FAILURE, APPEND_ALL_ENTITIES, APPEND_ENTITIES_FAILURE } from '../../tamalelibs/redux/note.actions';
import { MessageService } from '../../services/message.service';
import { MessageType } from '../../tamalelibs/models/message.model';
import { NotificationStyles, NotificationOptions } from '../../widgets/notification/notification.model';
import { ToastService } from '../../widgets/toast/toast.service';
import { businessConstants } from '../../constants/business.constants';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';
import { AppState } from '../../redux';
import { ThreadEntry } from '../../tamalelibs/models/thread-entry.model';
import { QueryInfo } from '../../pages/research/research.view-model';

@Component({
    selector: 'tam-append-entities-dialog',
    templateUrl: './append-entities-dialog.component.html',
    styleUrls: ['./append-entities-dialog.component.scss']
})
export class AppendEntitiesDialogComponent implements OnInit, OnDestroy {
    config: any;
    entityList: Array<EntityBrief> = [];
    entityConfig: TemplateControlConfig;
    showRequiredField = false;
    opened: boolean;

    selectedEntityIds: Array<string> = [];
    selectedItems: ThreadEntry[] = [];
    threadListTotalCount: number;
    widgetSubject = new Subject<TemplateControlValue>();

    private _appendEntityType: AppendEntitiesDialogType;
    private _queryInfo: QueryInfo = new QueryInfo();
    private _destroySubscriptions: Array<Subscription> = [];
    private _threadListIDs: string;

    constructor(
        private _appendEntitiesDialogService: AppendEntitiesDialogService,
        private _messageService: MessageService,
        private _store: Store<AppState>,
        private _toast: ToastService,
    ) {
    }

    ngOnInit() {
        const self = this;
        this._initEntityConfig();
        this.widgetSubject = new Subject<TemplateControlValue>();
        this._destroySubscriptions.push(
            this.widgetSubject.subscribe(this._onWidgetValueChanged.bind(this))
        );

        this._destroySubscriptions.push(
            this._appendEntitiesDialogService.dialogOpen$
                .pipe(
                    catchError((err) => {
                        return of(null);
                    }),
                )
                .subscribe(action => {
                    this._appendEntityType = action.openType;
                    if (action.openType === AppendEntitiesDialogType.AppendEntities) {
                        this.selectedEntityIds = [];
                        this.selectedItems = action.selectedItems;
                        this.threadListTotalCount = this.selectedItems.length;
                    } else if (action.openType === AppendEntitiesDialogType.AppendAllEntities) {
                        this._threadListIDs = action.threadListIDs;
                        this.threadListTotalCount = action.threadListTotalCount;
                        this._queryInfo = action.queryInfo;
                    }
                    this.showRequiredField = false;
                    this.opened = true;
                }),
            // Get notification by websocket.
            this._messageService.messageSubject$.subscribe(action => this._onMessageAction(action)),

            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => {
                this._onSubscribeActionMessage(action);
            }),
        );
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    clickClose() {
        this.opened = false;
    }

    onBackClick() {
        this.opened = false;
    }

    onAppend() {
        const entityIDList = this.selectedEntityIds.map(id => `"${id}"`).join(',');
        const entryConfig = `
                {
                    "fields": [
                        "entity"
                    ],
                    "values": {
                        "newValue": [${entityIDList}]
                    },
                    "operation": "append"
                }
                `;
        if (this._appendEntityType === AppendEntitiesDialogType.AppendEntities) {
            const entryIDList = Array.from(new Set(this.selectedItems.map(item => item.threadEntryThreadId))).join(';');
            if (entityIDList.length > 0) {
                this._store.dispatch({
                    type: APPEND_ENTITIES,
                    payload: {
                        entryIDList: entryIDList,
                        entryConfig: entryConfig
                    }
                });
                this.opened = false;
                this.showRequiredField = false;
                this.selectedEntityIds = [];
            } else {
                this.showRequiredField = true;
            }
        } else {
            const entryIDList = this._threadListIDs;
            if (entityIDList.length > 0) {
                this._store.dispatch({
                    type: APPEND_ALL_ENTITIES,
                    payload: {
                        entryIDList: entryIDList,
                        entryConfig: entryConfig,
                        queryInfo: this._queryInfo
                    }
                });
                this.opened = false;
                this.showRequiredField = false;
                this.selectedEntityIds = [];
            } else {
                this.showRequiredField = true;
            }
        }
    }

    private _initEntityConfig() {
        this.entityConfig = new TemplateControlConfig();
        this.entityConfig.componentType = 'entity-input';
        this.entityConfig.isLabelHidden = true;
        this.entityConfig.width = 0;
        this.entityConfig.prefixId = '';
        this.entityConfig.source = null;
        this.entityConfig.noteAction = NoteActions.appendEntity;
    }

    /**
     * Get information from web socket
     * @param action 
     */
    private _onMessageAction(action) {
        if (action.type === MessageType.APPEND_ENTITIES) {
            const results = action?.payload?.results;
            if (results) {
                if (results?.total === results.successfulEntryIDTotal) {
                    const _appendEntityToast: NotificationOptions = {
                        message: businessConstants.note.AppendEntitySuccess,
                        style: NotificationStyles.Info
                    };
                    this._toast.notify(_appendEntityToast);
                }
                this.opened = false;
                setTimeout(() => {
                    this._appendEntitiesDialogService.actions$.next({ type: 'Reload' });
                }, 100);
            }
        }
    }

    private _onWidgetValueChanged(data: TemplateControlValue) {
        // show error message when user selects an entity and then remove it
        if (data.actualValue.length === 0 && this.selectedEntityIds.length > 0) {
            this.showRequiredField = true;
        }
        this.selectedEntityIds = [];
        if (data.componentType === 'entity-input' && data.actualValue.length > 0) {
            data.actualValue.forEach(res => {
                this.selectedEntityIds.push(res['id']);
            });
            if (this.selectedEntityIds.length > 0) {
                this.showRequiredField = false;
            } else {
                this.showRequiredField = true;
            }
        }
    }

    private _onSubscribeActionMessage(action) {
        if (action.type === APPEND_ENTITIES_FAILURE || action.type === APPEND_ALL_ENTITIES_FAILURE) {
            const toastMsg = businessConstants.note.AppendEntityFailed;
            this._toast.notify({
                message: toastMsg,
                style: NotificationStyles.Error
            });
        }
    }
}
